import { Box, Typography } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { TournamentEntity } from 'app/interfaces/entity';
import { dispatchUnwrap } from 'app/utils/store';
import { useTeamDetails, useTournamentDetails } from 'modules/tournaments/hooks';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import {
  getTournamentGameUrl,
  joinTournament,
} from 'modules/tournaments/store/current-tournament/repository';
import React, { FC, useCallback } from 'react';
import { ActionGuard } from 'shared/components/ActionGuard';
import { FINISHED_TOURNAMENT_STATUSES } from 'modules/tournaments/constants';
import { useJoinAsyncTournamentForm } from 'modules/tournaments/hooks/use-join-async-tournament-form';
import { TournamentAsyncPassphraseBlock } from '../TournamentAsyncPassphraseBlock';
import { useStyles } from './TournamentModalActions.styles';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { WithPaidActionButton } from 'modules/game-payments/components';
import { FormProvider } from 'react-hook-form';

interface TournamentModalActionsProps {
  tournament: TournamentEntity;
  status: TournamentStatus;
}

export const TournamentModalActions: FC<TournamentModalActionsProps> = ({ tournament, status }) => {
  const { classes } = useStyles();
  const { isFull, isCurrentUserJoined } = useTournamentDetails();
  const { isScoreSubmitted } = useTeamDetails();

  const joining = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Joining,
  });
  const loading = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Loading,
  });

  const { form } = useJoinAsyncTournamentForm();

  const entryFee = tournament?.prizeAndDistribution?.playerEntryFee;

  const playGameHandler = useCallback(() => {
    dispatchUnwrap<string>(
      getTournamentGameUrl(tournament?.id),
      {
        success: (gameUrl) => window.open(gameUrl, '_blank'),
      },
      true
    );
  }, [tournament?.id]);

  const joinTournamentHandler = useCallback(() => {
    if (!tournament?.id) {
      return;
    }

    dispatchUnwrap(
      joinTournament({ identifier: tournament?.id, data: form.getValues() }),
      { success: playGameHandler },
      true
    );
  }, [tournament?.id, form, playGameHandler]);

  if (!tournament || FINISHED_TOURNAMENT_STATUSES.includes(status)) {
    return null;
  }

  const playComponent = (
    <ActionGuard>
      <LoaderButton
        className={classes.playButton}
        fullWidth
        size="medium"
        color="primary"
        variant="contained"
        loading={loading}
        onClick={playGameHandler}
        disabled={isScoreSubmitted || loading}
      >
        Play Game
      </LoaderButton>
    </ActionGuard>
  );

  const joinComponent = (
    <FormProvider {...form}>
      <Box className={classes.joinActions}>
        {isFull && <Typography className={classes.fullTeamsText}>Tournament is full</Typography>}
        <TournamentAsyncPassphraseBlock />
        <ActionGuard>
          <WithPaidActionButton
            label="Play"
            tooltipText="Join Tournament"
            amount={entryFee}
            onClick={joinTournamentHandler}
            isLoading={joining}
          >
            {({ isCanNotEnter, text, onClick }) => (
              <ActionGuard>
                <LoaderButton
                  className={classes.playButton}
                  fullWidth
                  size="medium"
                  color="primary"
                  variant="contained"
                  loading={joining}
                  disabled={isFull || joining || isCanNotEnter}
                  onClick={onClick}
                >
                  {text}
                </LoaderButton>
              </ActionGuard>
            )}
          </WithPaidActionButton>
        </ActionGuard>
      </Box>
    </FormProvider>
  );

  return <>{isCurrentUserJoined ? playComponent : joinComponent}</>;
};
